<template>
    <div class="kv-input" :class="[sizeClass, `kv-${variant}-input`, { 'kv-disabled': disabled, 'kv-error': hasError, 'kv-readonly': readonly, 'kv-has-label': !!label }]" @click="$emit('click')">
        <label v-if="label" class="kv-label" :for="id">{{ label }}</label>

        <div class="kv-input-wrapper">
            <Icon v-if="prependIcon" :icon-class="prependIcon" :size="calculatedIconSize" />
            <slot name="prepend"></slot>

            <div class="kv-input-field">
                <template v-if="!editable">
                    <div class="kv-input-custom-content">
                        <slot name="custom-content"></slot>
                    </div>
                </template>

                <input v-else ref="kvInputRef" @keydown="onKeydown" :id="id" :type="inputType" :disabled="disabled" :readonly="readonly" v-model="internalValue" @input="onInput" @change="$emit('change', internalValue)" @focus="$emit('focus', $event)" @blur="onBlur" :aria-invalid="hasError ? 'true' : 'false'" class="kv-native-input" :max="max" :min="min" />
                <span v-show="showPlaceholder && editable" class="kv-placeholder">{{ placeholder }}</span>

                <!-- CONTROLES -->
                <div v-if="type === 'number' && !readonly && !disabled" class="kv-number-controls">
                    <span class="kv-control up" @click="increment">
                        <Icon icon-class="ui-chevron-up"></Icon>
                    </span>

                    <span class="kv-control down" @click="decrement">
                        <Icon icon-class="ui-chevron-down"></Icon>
                    </span>
                </div>
            </div>
            <slot name="append"></slot>
            <Icon v-if="appendIcon" :icon-class="appendIcon" :size="calculatedIconSize" />

        </div>

        <!-- <p v-if="hint && !hasError" class="kv-hint">{{ hint }}</p> -->
        <!-- <p v-if="hasError" class="kv-error-message">{{ errorMessage }}</p> -->
    </div>
</template>

<script>
import Icon from '../icon/icon.vue';

export default {
    name: 'KvInput',
    components: {
        Icon
    },
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: [String, Number],
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        type: {
            type: String,
            default: 'text'
        },
        variant: {
            type: String,
            default: 'tonal'
        },
        size: {
            type: String,
            default: 'regular'
        },
        placeholder: {
            type: String,
            default: ''
        },
        alwaysValidate: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        hint: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default() {
                return 'kv-input-' + Math.random().toString(36).substr(2, 9)
            }
        },
        validator: {
            // optional function: value => { valid: boolean, message: string }
            type: Function,
            default: null
        },
        min: {
            type: Number,
            default: null
        },
        max: {
            type: Number,
            default: null
        },
        prependIcon: {
            type: String,
            default: null,
        },
        appendIcon: {
            type: String,
            default: null,
        },
        money: {
            type: Boolean,
            default: false
        },
        currency: {
            type: String,
            default: 'BRL'
        },
        editable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            internalValue: this.value,
            touched: false || this.alwaysValidate,
            validationResult: { valid: true, message: '' }
        }
    },
    computed: {
        sizeClass() {
            return `kv-${this.size}-input`;
        },
        inputType() {
            if (this.money) return 'tel';
            return this.type;
        },
        showPlaceholder() {
            return this.placeholder && (this.internalValue === null || this.internalValue === undefined || this.internalValue === '');
        },
        hasError() {
            return !this.validationResult.valid && this.touched
        },
        errorMessage() {
            return this.validationResult.message || ''
        },
        calculatedIconSize() {
            // Se o dev passou manualmente, respeita o valor
            if (this.iconSize) return this.iconSize;

            // Caso contrário, define automático
            switch (this.size) {
                case "small":
                    return 1;
                case "large":
                    return 4;
                default:
                    return 2;
            }
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (this.money) {
                    const currentNumericValue = this.unformatCurrency(this.internalValue);

                    if (currentNumericValue !== Number(newValue)) {
                        this.internalValue = this.formatCurrency(newValue);
                    }
                } else {
                    this.internalValue = newValue;
                }
                this.validate();
            }
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        formatCurrency(value) {
            if (value === '' || value === null || value === undefined) return '';

            const number = Number(value);
            if (isNaN(number)) return '';

            const locale = this.currency === 'USD' ? 'en-US' : 'pt-BR';
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.currency || 'BRL',
                minimumFractionDigits: 2
            }).format(number);
        },

        // Transforma "R$ 1.234,56" em 1234.56
        unformatCurrency(str) {
            if (!str) return 0;
            // Se já vier numérico (caso raro), retorna ele mesmo
            if (typeof str === 'number') return str;

            // Remove tudo que não for dígito e sinal de menos
            // Tratamento simples: assume input pt-BR onde centavos são os ultimos 2 digitos
            const cleanStr = str.replace(/\D/g, '');

            // Divide por 100 para considerar os centavos
            return Number(cleanStr) / 100;
        },
        increment() {
            if (this.money) return;

            let value = Number(this.internalValue) || 0
            const next = value + 1
            if (this.max !== null && next > this.max) return
            this.internalValue = next
            this.$emit('input', this.internalValue)
            this.validate()
        },

        decrement() {
            if (this.money) return;

            let value = Number(this.internalValue) || 0
            const next = value - 1
            if (this.min !== null && next < this.min) return
            this.internalValue = next
            this.$emit('input', this.internalValue)
            this.validate()
        },
        focus() {
            if (this.$refs.kvInputRef) {
                this.$refs.kvInputRef?.focus()
            }
        },
        onKeydown(event) {
            if (this.type === 'number' && !this.money) {
                const invalidKeys = ['-', '+', 'e', 'E']
                if (invalidKeys.includes(event.key)) {
                    event.preventDefault()
                }
            }
            this.$emit('keydown', event)
        },

        // MUDANÇA 4: Manipulação pesada no input para Money
        onInput(event) {
            // Lógica para Money
            if (this.money) {
                // 1. Pega o valor digitado cru (ex: "R$ 10,0" + digita "5" -> "R$ 10,05")
                let rawValue = event.target.value;

                // 2. Converte para o número real (float)
                const numericValue = this.unformatCurrency(rawValue);

                // 3. Formata de volta para String Bonita
                const formattedValue = this.formatCurrency(numericValue);

                // 4. Atualiza o visual (internalValue)
                // Nota: Em Vue 2, as vezes precisamos forçar a atualização se o valor 
                // antes e depois da formatação resultarem na mesma string para o VDOM, 
                // mas o cursor do input mudou.
                this.internalValue = formattedValue;

                // IMPORTANTE: Se o usuário apagar tudo, manda string vazia ou 0
                if (numericValue === 0) {
                    // Opcional: Decidir se quer mandar 0 ou ''
                    // this.internalValue = ''; 
                }

                // 5. Emite o NUMBER para o pai (v-model espera o número limpo)
                this.$emit('input', numericValue);

                // 6. Força o input do HTML a mostrar o valor formatado imediatamente
                // Isso cria o efeito de "arrastar os números" da direita pra esquerda
                this.$refs.kvInputRef.value = formattedValue;

            } else {
                // Lógica Original para Text/Number
                if (this.type === 'number' && this.internalValue !== '') {
                    const val = parseFloat(this.internalValue);
                    this.internalValue = isNaN(val) ? this.internalValue : val;
                }
                this.$emit('input', this.internalValue)
            }

            this.validate()
        },
        onBlur(event) {
            this.touched = true
            this.validate()
            this.$emit('blur', event)
        },
        validate() {
            if (typeof this.validator === 'function') {
                try {
                    // Para money, talvez você queira validar o número, não a string formatada?
                    // Atualmente validamos o this.internalValue.
                    // Se for money, internalValue é String ("R$ 10,00").
                    // Se seu validator espera numero, precisaria passar this.unformatCurrency(this.internalValue)

                    const valueToValidate = this.money
                        ? this.unformatCurrency(this.internalValue)
                        : this.internalValue;

                    const result = this.validator(valueToValidate)

                    if (typeof result === 'boolean') {
                        this.validationResult = { valid: result, message: result ? '' : this.t('inputs-validation-invalid_value') }
                    } else if (result && typeof result === 'object') {
                        this.validationResult = {
                            valid: result.valid,
                            message: result.message || ''
                        }
                    } else {
                        this.validationResult = { valid: true, message: '' }
                    }
                } catch (err) {
                    this.validationResult = { valid: false, message: err.message || this.t('inputs-validation-validation_error') }
                }
            } else {
                this.validationResult = { valid: true, message: '' }
            }
            return this.validationResult.valid
        }
    },
    mounted() {
        // initial validation
        if (this.money && this.value) {
            this.internalValue = this.formatCurrency(this.value);
        } else {
            this.validate()
        }
    }
}
</script>

<style lang="less">
.kv-input {
    display: flex;
    flex-direction: column;
    font-family: inherit;
    height: 36px;
    font-size: 0.9rem;

    &.kv-readonly .kv-native-input {
        cursor: inherit;
    }

    &.kv-tinted-input .kv-input-wrapper {
        border-color: #2AAA4C;
        background-color: #F3FAF4;

        .kv-native-input {
            color: #1D7836;
        }
    }

    &.kv-outlined-input .kv-input-wrapper {
        border-color: #E5E7EB;
        background-color: #FFFFFF;

        .kv-native-input {
            color: #1F2A37;
        }
    }

    &.kv-small-input {
        font-size: 13px;
        height: 30px; // Altura compacta para acompanhar o small button
    }

    &.kv-large-input {
        font-size: 14px;
        height: 42px;
    }

    // The fixed height above sizes the field alone. With a label stacked on
    // top the root has to grow, so the height moves down to the wrapper.
    &.kv-has-label {
        height: auto;

        .kv-input-wrapper {
            height: 36px;
        }

        &.kv-small-input .kv-input-wrapper {
            height: 30px;
        }

        &.kv-large-input .kv-input-wrapper {
            height: 42px;
        }
    }

    .kv-label {
        margin-bottom: 6px;
        font-size: inherit;
    }

    .kv-input-wrapper {
        display: flex;
        align-items: center;
        position: relative;
        background-color: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        color: #1f2a37;
        padding: 10px;
        gap: 8px;
        height: 100%;
    }

    .kv-native-input {
        flex: 1 1 auto;
        border: none;
        outline: none;
        background: transparent;
        padding: 0;
        line-height: 1;
        width: 100%;
    }

    .kv-hint {
        margin-top: 6px;
        font-size: 0.85rem;
        color: #6b7280;
    }

    .kv-error-message {
        margin-top: 6px;
        font-size: 0.85rem;
        color: #b91c1c;
    }

    &.kv-error .kv-input-wrapper {
        border-color: #f87171;
    }

    .kv-disabled {
        opacity: 0.6;
        pointer-events: none;
    }

    .kv-input-field {
        display: flex;
        position: relative;
        font-size: inherit;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;

        .kv-input-custom-content {
            width: 100%;
            display: flex;
            align-items: center;
            overflow: hidden; 
        }
    }

    .kv-placeholder {
        position: absolute;
        color: #9ca3af;
        white-space: nowrap;
        opacity: 0;
        left: 0;
        pointer-events: none;
        top: 50%;
        transform: translateY(-50%);
        font-size: inherit;
        line-height: 1;
    }

    .kv-placeholder {
        opacity: 1;
    }

    // NUMBER
    .kv-native-input[type='number'] {
        -moz-appearance: textfield;
    }

    .kv-native-input[type='number']::-webkit-inner-spin-button,
    .kv-native-input[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .kv-number-controls {
        display: flex;
        flex-direction: column;
        margin-left: 4px;
    }

    .kv-control {
        all: unset;
        cursor: pointer;
        font-size: 10px;
        line-height: 1;
        padding: 0;
        color: #6b7280;
        text-align: center;
        display: flex;

        &:hover {
            color: #1D7836;
        }

        &:active {
            transform: scale(0.9);
        }
    }
}
</style>